/**
 * Kalori for Brands — 飲食ブランド向け管理画面（brands.kalori.jp）。
 *
 * 入れるのは運営が承認したメールアドレスだけ（招待 URL → 以後はメールの確認コード・
 * `docs/architecture/b2b-onboarding.md` §8）。この画面に「新規登録」は無い — 始めたい事業者は
 * 同じホストの `/apply`（利用申請・審査制）へ案内する（console / coaches と同じ）。
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api, type Me } from "./api";
import { Link, match, navigate, useLocation } from "./router";
import { Login } from "./screens/Login";
import { Invite } from "./screens/Invite";
import { Apply } from "./screens/Apply";
import { Home } from "./screens/Home";
import { Catalog } from "./screens/Catalog";
import { Product } from "./screens/Product";
import { Entries } from "./screens/Entries";
import { EntryEditor } from "./screens/EntryEditor";
import { FeedbackList, FeedbackProvider, FeedbackThreadView, useFeedback } from "./screens/Feedback";
import { BulkInput } from "./screens/BulkInput";
import { MyIngredients } from "./screens/MyIngredients";
import { Changes } from "./screens/Changes";
import { PrintTable } from "./screens/PrintTable";
import { PublicPage } from "./screens/PublicPage";
import { KaloriLogo } from "./KaloriLogo";

/** ログインしていなくても開ける画面。 */
const PUBLIC_PATHS = [/^\/login\/?$/, /^\/invite\/[^/]+\/?$/, /^\/apply\/?$/];

export function App() {
  const loc = useLocation();
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [bootError, setBootError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await api.me();
    if (r.ok) {
      setMe(r.data);
      setBootError(null);
    } else if (r.status === 401) {
      setMe(null);
    } else {
      setBootError(r.error);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isPublic = PUBLIC_PATHS.some((re) => re.test(loc.path));
  useEffect(() => {
    if (me === null && !isPublic) navigate("/login", { replace: true });
  }, [me, isPublic]);

  const signedIn = (next: Me) => {
    setMe(next);
    navigate("/", { replace: true });
  };

  if (bootError) {
    return (
      <main className="center-page">
        <p className="brand-mark">
          <KaloriLogo height={22} label="Kalori" className="brand-logo" /> <span className="brand-sub">for Brands</span>
        </p>
        <p className="error">{bootError}</p>
        <button className="btn" onClick={() => void refresh()}>
          再読み込み
        </button>
      </main>
    );
  }
  if (me === undefined) return <main className="center-page muted">読み込んでいます…</main>;

  // 利用申請はログインの有無に関係なく開ける（LP の CTA の着地点）
  if (/^\/apply\/?$/.test(loc.path)) return <Apply query={loc.query} />;
  const invite = match("/invite/:token", loc.path);
  if (invite) return <Invite token={invite.token} onSignedIn={signedIn} signedInAs={me} />;
  if (!me) return <Login onSignedIn={signedIn} />;

  return (
    <FeedbackProvider initialUnread={me.unreadFeedback ?? 0}>
      <Shell me={me} path={loc.path} onSignedOut={() => setMe(null)}>
        <Routes path={loc.path} query={loc.query} me={me} />
      </Shell>
    </FeedbackProvider>
  );
}

function Routes({ path, query, me }: { path: string; query: URLSearchParams; me: Me }) {
  let m: Record<string, string> | null;
  if (path === "/" || path === "/login") return <Home me={me} />;
  if (path === "/catalog") return <Catalog query={query} />;
  if ((m = match("/catalog/:productId", path))) return <Product productId={Number(m.productId)} />;
  if (path === "/entries") return <Entries />;
  if (path === "/entries/bulk") return <BulkInput />;
  if (path === "/ingredients") return <MyIngredients />;
  if (path === "/changes") return <Changes />;
  if (path === "/print") return <PrintTable />;
  if (path === "/public-page") return <PublicPage />;
  if ((m = match("/entries/:id", path))) return <EntryEditor id={m.id} />;
  if (path === "/feedback") return <FeedbackList />;
  if ((m = match("/feedback/:id", path))) return <FeedbackThreadView id={m.id} />;
  return (
    <section>
      <h1>ページが見つかりません</h1>
      <p>
        <Link href="/">ホームへ戻る</Link>
      </p>
    </section>
  );
}

const NAV = [
  { href: "/", label: "ホーム", active: (p: string) => p === "/" },
  { href: "/catalog", label: "掲載中のメニュー", active: (p: string) => p.startsWith("/catalog") },
  { href: "/entries", label: "公式値の入力", active: (p: string) => p.startsWith("/entries") || p === "/print" },
  { href: "/ingredients", label: "食材・部品", active: (p: string) => p.startsWith("/ingredients") },
  { href: "/changes", label: "変化", active: (p: string) => p.startsWith("/changes") },
  { href: "/feedback", label: "お問い合わせ", active: (p: string) => p.startsWith("/feedback") },
];

function Shell({ me, path, onSignedOut, children }: { me: Me; path: string; onSignedOut: () => void; children: ReactNode }) {
  const { open: openFeedback, unread } = useFeedback();
  const logout = async () => {
    await api.logout();
    onSignedOut();
    navigate("/login", { replace: true });
  };
  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-inner">
          <Link href="/" className="brand">
            <KaloriLogo height={20} label="Kalori" className="brand-logo" /> <span className="brand-sub">for Brands</span>
          </Link>
          <nav className="nav" aria-label="メインメニュー">
            {NAV.map((n) => (
              <Link key={n.href} href={n.href} className={n.active(path) ? "nav-link active" : "nav-link"} aria-current={n.active(path) ? "page" : undefined}>
                {n.label}
                {/* 運営から返信があって、まだ開いていないお問い合わせ */}
                {n.href === "/feedback" && unread > 0 && (
                  <span className="nav-badge" aria-label={`未読 ${unread} 件`}>
                    {unread}
                  </span>
                )}
              </Link>
            ))}
          </nav>
          <div className="account">
            {/* どの画面からでも 1 クリックで送れる場所 */}
            <button className="btn small-btn" onClick={() => openFeedback()}>
              ご意見・ご要望
            </button>
            <span className="account-name" title={me.email}>
              {me.merchant.name}
            </span>
            <button className="btn-link" onClick={() => void logout()}>
              ログアウト
            </button>
          </div>
        </div>
      </header>
      <main className="content">{children}</main>
      <footer className="footer muted">
        ご不明点・ご要望は画面上部の「ご意見・ご要望」から（お急ぎは <a href="mailto:contact@kalbi.jp">contact@kalbi.jp</a>・{me.email} でログイン中）
      </footer>
    </div>
  );
}
